import { AriaComponent } from 'aria-components';
import {
  interactiveChildren,
  keyCodes,
  getFirstAndLastItems,
  tabIndexDeny,
  tabIndexAllow,
  setUniqueId,
} from 'aria-components/utils';
import toArray from 'js/utils/toArray';

/**
 * Class to set up a programmatic Dialog element to be displayed without user interaction.
 */
export default class ProgrammaticDialog extends AriaComponent {
  /**
   * Create a Dialog.
   * @constructor
   *
   * @param {object} config The config object.
   */
  constructor(target, config) {
    super(config);

    // A target element is required.
    if (null == target) {
      throw new Error('Configuration error: A target element is required.');
    }

    /**
     * The dialog element.
     *
     * @type {HTMLElement}
     */
    this.target = target;

    /**
     * The component name.
     *
     * @type {string}
     */
    this.componentName = 'ProgrammaticDialog';

    /**
     * Options shape.
     *
     * @type {object}
     */
    const options = {
      /**
       * The element(s) to be hidden when the Dialog is visible. The elements
       * wrapping all site content with the sole exception of the dialog element.
       *
       * @type {HTMLElement|NodeList|Array}
       */
      content: [],

      /**
       * Callback to run after the component initializes.
       *
       * @callback initCallback
       */
      onInit: () => {},

      /**
       * Callback to run after component state is updated.
       *
       * @callback stateChangeCallback
       */
      onStateChange: () => {},

      /**
       * Callback to run after the component is destroyed.
       *
       * @callback destroyCallback
       */
      onDestroy: () => {},
    };

    // Merge config options with defaults and save all as instance properties.
    Object.assign(this, options, config);

    // Bind class methods
    this.init = this.init.bind(this);
    this.handleTargetKeydown = this.handleTargetKeydown.bind(this);
    this.hideOnOutsideClick = this.hideOnOutsideClick.bind(this);
    this.handleKeydownEsc = this.handleKeydownEsc.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);
    this.destroy = this.destroy.bind(this);
    this.stateWasUpdated = this.stateWasUpdated.bind(this);
    this.setInteractiveChildren = this.setInteractiveChildren.bind(this);
    this.setDialogController = this.setDialogController.bind(this);
    this.controllerClick = this.controllerClick.bind(this);

    /*
     * Initialize the component.
     */
    this.init();
  }

  /**
   * Set the Dialog controller.
   *
   * By default, the Dialog will not have a controller; use this method on an
   * instance to add a controller.
   */
  setDialogController(controller) {
    this.controller = controller;
    super.setSelfReference([this.controller]);

    /**
     * Check if the controller is a button, but only if it doesn't already have
     * a role attribute, since we'll be adding the role and allowing focus.
     *
     * @type {bool}
     */
    this.controllerIsNotAButton = (
      'BUTTON' !== this.controller.nodeName
      && null === this.controller.getAttribute('role')
    );

    /*
     * Use the button role on non-button elements.
     */
    if (this.controllerIsNotAButton) {
      // https://www.w3.org/TR/wai-aria-1.1/#button
      this.controller.setAttribute('role', 'button');
      this.controller.setAttribute('tabindex', '0');
    }

    this.controller.setAttribute('aria-expanded', 'false');

    this.controller.addEventListener('click', this.controllerClick);
    this.controller.addEventListener('keydown', this.controllerKeydown);
  }

  /**
   * Collect the Dialog's interactive child elements.
   */
  setInteractiveChildren() {
    this.interactiveChildElements = interactiveChildren(this.target);

    const [
      firstInteractiveChild,
      lastInteractiveChild,
    ] = getFirstAndLastItems(this.interactiveChildElements);

    // Save as instance properties.
    this.firstInteractiveChild = firstInteractiveChild;
    this.lastInteractiveChild = lastInteractiveChild;
  }

  /**
   * Set the component's DOM attributes and event listeners.
   */
  init() {
    // Get the content items if none are provided.
    if (0 === this.content.length || null == this.content) {
      this.content = Array.from(document.body.children)
        .filter((child) => ! child.contains(this.target));
    } else {
      this.content = toArray(this.content);
    }

    // If no content is found.
    if (0 === this.content.length) {
      // eslint-disable-next-line max-len
      throw new Error('Configuration error: The Dialog target should not be within the main site content.');
    }

    /*
     * A reference to the class instance added to the target element to enable
     * external interactions with this instance.
     */
    super.setSelfReference([this.target]);

    /*
     * Collect the Dialog's interactive child elements. This is an initial pass
     * to ensure values exists, but the interactive children will be collected
     * each time the dialog opens, in case the dialog's contents change.
     */
    this.setInteractiveChildren();

    // Focusable content should initially have tabindex='-1'.
    tabIndexDeny(this.interactiveChildElements);

    // Add target attribute.
    setUniqueId(this.target);

    /*
     * Set the taget as hidden by default. Using the `aria-hidden` attribute,
     * rather than the `hidden` attribute, means authors must hide the target
     * element via CSS.
     */
    this.target.setAttribute('aria-hidden', 'true');
    this.target.setAttribute('hidden', '');

    // Set additional attributes.
    this.target.setAttribute('role', 'dialog');
    this.target.setAttribute('aria-modal', 'true');

    // Add event listeners.
    this.target.addEventListener('keydown', this.handleTargetKeydown);

    /**
     * Set initial state.
     *
     * @type {object}
     */
    this.state = { expanded: false };

    /* Run {initCallback} */
    this.onInit.call(this);
  }

  /**
   * Update element attributes and event listeners when the Popup's state changes.
   * @todo Set target attributes and update roving tabindex.
   *
   * @param {Object} state The component state.
   */
  stateWasUpdated() {
    const { expanded } = this.state;
    const contentLength = this.content.length;

    this.setInteractiveChildren();

    if (expanded) {
      // Allow focus on the target element.
      this.target.setAttribute('tabindex', '0');

      // Hide the outside content.
      for (let i = 0; i < contentLength; i += 1) {
        this.content[i].setAttribute('aria-hidden', 'true');
      }

      // Update target element.
      this.target.setAttribute('aria-hidden', 'false');
      this.target.removeAttribute('hidden');

      if (null != this.controller) {
        this.controller.setAttribute('aria-expanded', 'true');
      }

      tabIndexAllow(this.interactiveChildElements);

      this.target.focus();
    } else {
      // Disallow focus on the target element.
      this.target.setAttribute('tabindex', '-1');

      // Un-hide the outside content.
      for (let i = 0; i < contentLength; i += 1) {
        this.content[i].removeAttribute('aria-hidden');
      }

      // Update target element.
      this.target.setAttribute('aria-hidden', 'true');
      this.target.setAttribute('hidden', '');

      // Focusable content should have tabindex='-1' or be removed from the DOM.
      tabIndexDeny(this.interactiveChildElements);

      if (null != this.controller) {
        this.controller.setAttribute('aria-expanded', 'false');
        this.controller.focus();
      }
    }

    /* Run {stateChangeCallback} */
    this.onStateChange.call(this, this.state);
  }

  /**
   * Close the dialog on when users click outside of the Dialog element.
   *
   * By default, the Dialog will not close on outside click. This is by design,
   * but can be enabled manually by including an event listener with this method
   * as the callback.
   *   document.body.addEventListener('click', dialog.hideOnOutsideClick)
   *
   * @param {Event} event The Event object.
   */
  hideOnOutsideClick(event) {
    const { expanded } = this.state;
    const { target: clicked } = event;

    if (
      expanded && ! this.target.contains(clicked)
      && (null != this.controller && ! this.controller.contains(clicked))
    ) {
      this.hide();
    }
  }

  /**
   * Close the dialog on ESC key press.
   *
   * By default, the Dialog will not close with the ESC key. This is by design,
   * but can be enabled manually by including an event listener with this method
   * as the callback.
   *   document.body.addEventListener('keydown', dialog.handleKeydownEsc)
   *
   * @param {Event} event The Event object.
   */
  handleKeydownEsc(event) {
    const { ESC } = keyCodes;
    const { keyCode } = event;

    if (ESC === keyCode) {
      this.hide();
    }
  }

  /**
   * Trap key tabs within the dialog.
   *
   * @param {Event} event The Event object.
   */
  handleTargetKeydown(event) {
    const { TAB } = keyCodes;
    const { keyCode, shiftKey } = event;
    const { expanded } = this.state;

    if (expanded && keyCode === TAB) {
      const { activeElement } = document;

      if (shiftKey && this.firstInteractiveChild === activeElement) {
        event.preventDefault();
        /*
         * Move back from the first interactive child element to the last
         * interactive child element
         */
        this.lastInteractiveChild.focus();
      } else if (! shiftKey && this.lastInteractiveChild === activeElement) {
        event.preventDefault();
        /*
         * Move forward from the last interactive child element to the first
         * interactive child element.
         */
        this.firstInteractiveChild.focus();
      }
    }
  }

  /**
   * Handle controller keydown events to patch in button behaviors for
   * non-button elements.
   *
   * @param {Event} event The event object.
   */
  controllerKeydown(event) {
    const {
      SPACE,
      RETURN,
    } = keyCodes;
    const { keyCode } = event;

    if ([SPACE, RETURN].includes(keyCode)) {
      event.preventDefault();

      /*
       * Treat the Spacebar and Return keys as clicks in case the controller is
       * not a <button>.
       */
      this.show();
    }
  }

  /**
   * Handle controller clicks.
   *
   * @param {Event} event The event object.
   */
  controllerClick(event) {
    event.preventDefault();
    this.show();
  }

  /**
   * Destroy the Dialog and Popup.
   */
  destroy() {
    // Remove the references to the class instance.
    this.deleteSelfReferences();

    // Destroy the Dialog Popup.
    if (this.target.getAttribute('id').includes('id_')) {
      this.target.removeAttribute('id');
    }

    // Remove target attributes.
    this.target.removeAttribute('aria-hidden');
    this.target.removeAttribute('hidden');
    this.target.removeAttribute('tabindex');
    this.target.removeAttribute('role');
    this.target.removeAttribute('aria-modal');

    // Remove tabindex attribute.
    tabIndexAllow(this.interactiveChildElements);

    // Remove the `aria-hidden` attribute from the content wrapper.
    const contentLength = this.content.length;
    for (let i = 0; i < contentLength; i += 1) {
      this.content[i].removeAttribute('aria-hidden');
    }

    // Remove event listeners.
    this.target.removeEventListener('keydown', this.handleTargetKeydown);

    if (null != this.controller) {
      this.controller.removeAttribute('aria-expanded');

      // Remove role and tabindex added to a link controller.
      if (this.controllerIsNotAButton) {
        this.controller.removeAttribute('role');
        this.controller.removeAttribute('tabindex');
      }

      this.controller.removeEventListener('click', this.controllerClick);
      this.controller.removeEventListener('keydown', this.controllerKeydown);
    }

    // Reset initial state.
    this.state = { expanded: false };

    /* Run {destroyCallback} */
    this.onDestroy.call(this);
  }

  /**
   * Show the Dialog.
   */
  show() {
    this.setState({ expanded: true });
  }

  /**
   * Hide the Dialog.
   */
  hide() {
    this.setState({ expanded: false });
  }
}
